import React, { useState, useRef, useEffect, Fragment } from 'react';
import { hot } from 'react-hot-loader';
import Newsletter from '../Footer/Newsletter';
import Button from '../Elements/Button/Button';
import { removeEvent, purifier, setCookie, getCookie } from '../../utils/dom';
import { imageSizeTranslation } from '../../utils/image';
import { translate } from '../../utils/locale';
import { isRestricted } from '../../utils/restrict';
import { urlify, blackOrWhite } from '../../utils/string';
import s from './Notification.scss';

const Notification = ({ content, type, setup, newPage = [], reshowNotification = false, ...props }) => {
	const notifications = content.filter((n) => (!n.countryRestrict || (n.countryRestrict && !isRestricted(setup, { selectedCountries: n.countryRestrict })))
		&& (!!n?.text || !!n?.messages || !!n?.image || !!n?.title)
		&& n.active); // only notifications which are set.
	const [delay, delays] = useState({});
	const [elementRemoved, removingElement] = useState(false);
	const [showMessage, toggleMessage] = useState(true);
	const notificationInner = useRef(null);
	let Wrapper = ({ children }) => children;
	let isClosing = false;


	if (type === 'campaign') Wrapper = ({ children }) => <div className={s.notification_scroller}>{ children }</div>;

	useEffect(() => {
		notifications.forEach((item) => {
			const notificationName = urlify(item.name);
			if (item.delay) {
				delays((prevState) => ({
					...prevState,
					[notificationName]: false,
				}));
				setTimeout(() => {
					delays((prevState) => ({
						...prevState,
						[notificationName]: true,
					}));
				}, parseInt(item.delay, 10) * 1e3);
			}
		});
	}, []);

	useEffect(() => {
		isClosing = false;
		removingElement(false);
		toggleMessage(true);
	}, [reshowNotification]);

	useEffect(() => {
		const started = window.performance.now();

		const initiateSiteListeners = () => {
			if (!notifications && started + 2000 < window.performance.now()) {
				window.setTimeout(initiateSiteListeners, 200);
			}
		};

		initiateSiteListeners();

		return () => {
			removeEvent(document.querySelector('body'), 'click.notificationClose');
		};
	});

	if (!showMessage) {
		// Remove element
		isClosing = true;
		window.setTimeout(() => {
			removingElement(true);
			isClosing = false;
		}, 2000);

		if (elementRemoved) return null;
	}

	return (
		<div className={[s.notification, s[`notification__${type}`]].join(' ')}>
			<Wrapper>
				{
					(type === 'campaign' ? [...notifications, ...notifications, ...notifications] : notifications).map((item, i) => {
						const notificationName = urlify(item.name);
						const notificationInnerStyle = {};
						const notificationInnerClasses = [s.notification_inner];
						if (item.removeText) notificationInnerClasses.push(s.notification_inner__closeText);
						if (item.backgroundColor) notificationInnerStyle.backgroundColor = item.backgroundColor;
						if (item.textColor) notificationInnerStyle.color = blackOrWhite(item.backgroundColor);

						// Check for cookies which are already set.
						if ((item.removable || type === 'popup' || type === 'consumer') && item.cookieDuration) {
							let cookieIsSet = false;

							const cookies = typeof document !== 'undefined' ? getCookie(notificationName) : setup.cookies;
							if (cookies && typeof cookies === 'object') {
								if (cookies[notificationName]) cookieIsSet = true;
							} else if (cookies) {
								cookieIsSet = true;
							}

							// Still render notification if from server and cookie not set on page load.
							// This enables us to fade it out.
							if (cookieIsSet && !isClosing) return null;
						}
						if (item.delay && !delay[notificationName]) return null;

						const removeNotificationHandler = (e) => {
							if (e) e.preventDefault();

							setCookie(notificationName, 1, item.cookieDuration);
							toggleMessage(false);
						};

						const newsletterSubmitHandler = (autoRemove = false) => {
							if (autoRemove) {
								window.setTimeout(() => {
									removeNotificationHandler();
								}, 1500);
							}
						};

						const setConsumerType = (e) => {
							const value = e.currentTarget.value;
							setCookie(notificationName, value, item.cookieDuration);
							toggleMessage(false);
							props.consumerType(value);
						};

						return (
							<Fragment key={`${notificationName}_${i}`}>
								{ type === 'popup' && <button className={[s.notification_screen, (!showMessage ? s.notification_screen__exiting : null)].join(' ')} type="button" aria-label="Close popup" onClick={removeNotificationHandler} /> }
								{ type === 'consumer' && <div className={[s.notification_screen, (!showMessage ? s.notification_screen__exiting : null)].join(' ')} /> }
								<div ref={notificationInner} className={[...notificationInnerClasses, (!showMessage ? s.notification_inner__exiting : null)].join(' ')} style={notificationInnerStyle}>
									{
										item.messages && (
											<div className={s.notification_inner_messages}>
												{
													item.messages.map((message, j) => (
														<div key={message?.icon?.url || message.text} className={[s.notification_inner_message, (Math.floor(item.messages.length * newPage[0]) === j ? s.notification_inner_message__selected : null)].join(' ')}>
															{ message.icon && <img className={s.notification_inner_message_icon} src={message.icon.url} alt={message.icon.alt} height="24" /> }
															{ message.text && <span className={s.notification_inner_message_text} dangerouslySetInnerHTML={{ __html: purifier(translate(setup, message.text), {}, setup) }} /> }
														</div>
													))
												}
											</div>
										)
									}
									{/* item.minimizeTitle && minimizeMessage && <button className={s.notification_inner_minimizeTitle} type="button" onClick={expandeNotificationHandler}>{ translate(setup, item.minimizeTitle) }</button> */}
									{
										item.title && (
											<div className={s.notification_inner_consumer}>
												<svg width="89" height="13" viewBox="0 0 89 13" fill="none" xmlns="http://www.w3.org/2000/svg">
													<path d="M7.29454 12.184C12.0555 12.184 13.5045 11.379 13.5045 8.458V8.228H12.3775V8.412C12.3775 10.551 11.5725 11.149 7.22554 11.149H6.78854C2.02754 11.149 1.19954 10.39 1.19954 6.802V6.549H13.4125V5.33C13.4125 1.696 12.2395 0.775999 7.08754 0.775999H6.60454C1.29154 0.775999 0.0265353 1.65 0.0265353 6.319V6.595C0.0265353 11.172 1.19954 12.184 6.78854 12.184H7.29454ZM1.22254 5.537C1.31454 2.432 1.98154 1.811 6.67354 1.811H7.04154C11.5725 1.811 12.2625 2.455 12.2625 5.169V5.537H1.22254ZM39.1733 5.284C39.1733 1.696 38.0693 0.775999 34.0213 0.775999H33.6073C30.1113 0.775999 28.8693 2.133 28.4093 3.582H28.3863C28.0183 1.673 27.2133 0.775999 23.4183 0.775999H23.0733C19.9453 0.775999 18.6113 1.742 17.9903 3.237H17.9673V0.96H16.7943V12H17.9673V5.859C17.9673 2.708 19.6923 1.811 22.8433 1.811H23.1883C26.5693 1.811 27.3973 2.294 27.3973 5.859V12H28.5703V6.066C28.5703 2.777 30.2263 1.811 33.3773 1.811H33.7683C37.3103 1.811 38.0003 2.432 38.0003 5.882V12H39.1733V5.284ZM49.6109 12.184C54.3719 12.184 55.8209 11.379 55.8209 8.458V8.228H54.6939V8.412C54.6939 10.551 53.8889 11.149 49.5419 11.149H49.1049C44.3439 11.149 43.5159 10.39 43.5159 6.802V6.549H55.7289V5.33C55.7289 1.696 54.5559 0.775999 49.4039 0.775999H48.9209C43.6079 0.775999 42.3429 1.65 42.3429 6.319V6.595C42.3429 11.172 43.5159 12.184 49.1049 12.184H49.6109ZM43.5389 5.537C43.6309 2.432 44.2979 1.811 48.9899 1.811H49.3579C53.8889 1.811 54.5789 2.455 54.5789 5.169V5.537H43.5389ZM65.8957 12.184C70.7487 12.184 72.1517 11.379 72.1517 8.343V8.044H71.0247V8.251C71.0247 10.551 70.1507 11.149 65.8727 11.149H65.3897C60.5367 11.149 59.7777 10.436 59.7777 6.664V6.296C59.7777 2.478 60.4907 1.811 65.3897 1.811H65.8727C70.0587 1.811 70.9327 2.317 70.9327 4.594V4.755H72.0597V4.502C72.0597 1.466 70.8177 0.775999 65.8957 0.775999H65.3667C59.8467 0.775999 58.6047 1.627 58.6047 6.296V6.664C58.6047 11.287 59.8237 12.184 65.3667 12.184H65.8957ZM81.7188 11.149C76.7738 11.149 76.1068 10.574 76.1068 6.664V6.296C76.1068 2.386 76.7508 1.811 81.7188 1.811H82.2018C87.1468 1.811 87.7908 2.386 87.7908 6.296V6.664C87.7908 10.574 87.1468 11.149 82.2018 11.149H81.7188ZM82.2248 12.184C87.9058 12.184 88.9638 11.333 88.9638 6.664V6.296C88.9638 1.604 87.9058 0.775999 82.2248 0.775999H81.6958C76.0148 0.775999 74.9338 1.604 74.9338 6.296V6.664C74.9338 11.333 76.0148 12.184 81.6958 12.184H82.2248Z" fill="black" />
												</svg>
												<h4 className={s.notification_inner_consumer_title}>{ translate(setup, item.title) }</h4>
												<div className={s.notification_inner_consumer_choices}>
													<Button type="button" clickEventHandler={setConsumerType} value="private">{ translate(setup, 'consumerPrivate') }</Button>
													<Button type="button" clickEventHandler={setConsumerType} value="trade">{ translate(setup, 'consumerTrade') }</Button>
												</div>
											</div>
										)
									}
									{
										(item?.imageMobile || item?.image) && !item?.filmMobile && !item?.film && (
											<picture className={s.notification_inner_image}>
												{ item.imageMobile && <source {...imageSizeTranslation('4', item.imageMobile, '-', false)} media="(orientation: portrait)" /> }
												{ item?.image && <img {...imageSizeTranslation('4', item.image, '-', false)} /> }
											</picture>
										)
									}
									{
										(item?.filmMobile || item?.film) && (
											<>
												{ setup.breakPoint === 'mobile' && item?.filmMobile && <video className={s.notification_inner_image} src={item.filmMobile.url} autoPlay loop muted playsInline /> }
												{ setup.breakPoint !== 'mobile' && item?.film && <video className={s.notification_inner_image} src={item.film.url} autoPlay loop muted playsInline /> }
											</>
										)
									}
									{
										(item.text || item.includeNewsletter) && (
											<div className={s.notification_inner_content}>
												{ item.text && <div className={s.notification_inner_content_text} dangerouslySetInnerHTML={{ __html: purifier(translate(setup, item.text), {}, setup) }} /> }
												{ item.includeNewsletter && <div className={s.notification_inner_newsletter}><Newsletter callback={newsletterSubmitHandler} popup isClosing={isClosing} closePopup={removeNotificationHandler} /></div> }
											</div>
										)
									}
									{
										(item.removable || type === 'popup') && item.cookieDuration && (
											<a className={s.notification_inner_close} href="#close" onClick={removeNotificationHandler}>
												{ item.removeText ? translate(setup, item.removeText) : <span className={s.notification_inner_close_icon} /> }
											</a>
										)
									}
								</div>
							</Fragment>
						);
					})
				}
			</Wrapper>
		</div>
	);
};

export default hot(module)(Notification);
